export type Platform = 'mac' | 'windows' | 'linux';
export type Arch = 'arm64' | 'x64' | 'universal';

export interface DetectedPlatform {
  platform: Platform | null;
  arch: Arch | null;
  label: string;
}

/**
 * Best-effort guess at the visitor's OS and CPU, used only to pre-select a
 * recommended download. Every build stays visible, so a wrong guess is
 * cosmetic rather than a dead end.
 */
export function detectPlatform(): DetectedPlatform {
  const nav = navigator as Navigator & {
    userAgentData?: { platform?: string; architecture?: string; brands?: unknown[] };
  };
  const ua = navigator.userAgent;
  const uaPlatform = (nav.userAgentData?.platform ?? navigator.platform ?? '').toLowerCase();

  let platform: Platform | null = null;
  if (uaPlatform.includes('mac') || /Macintosh|Mac OS X/i.test(ua)) platform = 'mac';
  else if (uaPlatform.includes('win') || /Windows/i.test(ua)) platform = 'windows';
  else if (uaPlatform.includes('linux') || /Linux|X11/i.test(ua)) platform = 'linux';

  let arch: Arch | null = null;
  if (platform === 'mac') {
    // Browsers deliberately hide Apple Silicon behind an Intel user-agent, so
    // probe a WebGL renderer string instead — Apple GPUs only exist on M-series.
    arch = isAppleSilicon() ? 'arm64' : 'x64';
  } else if (platform === 'windows') {
    arch = /ARM|aarch64/i.test(ua) || nav.userAgentData?.architecture === 'arm' ? 'arm64' : 'x64';
  } else if (platform === 'linux') {
    arch = /aarch64|arm64/i.test(ua) ? 'arm64' : 'x64';
  }

  const labels: Record<Platform, string> = { mac: 'macOS', windows: 'Windows', linux: 'Linux' };
  const archLabel = arch === 'arm64' ? (platform === 'mac' ? 'Apple Silicon' : 'ARM64') : arch === 'x64' ? (platform === 'mac' ? 'Intel' : '64-bit') : '';

  return {
    platform,
    arch,
    label: platform ? `${labels[platform]}${archLabel ? ` · ${archLabel}` : ''}` : 'your computer',
  };
}

function isAppleSilicon(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
    if (!gl) return navigator.maxTouchPoints > 1;
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (!ext) return false;
    const renderer = String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) ?? '');
    return /Apple (M\d|GPU)/i.test(renderer);
  } catch {
    return false;
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unit]}`;
}
